import { SortDirection, TableHeaderColumn } from '../types/table'
import * as React from 'react'
import {
  isEmptySortValue,
  parseAmountValue,
  parseDateValue,
  parseDurationValue,
} from '@/modules/shared/utils/sortValues'

export const useSortableData = <T extends Record<string, any>>({
  data,
  tableColumns,
  includeActiveSorting = true,
  emptyValuesLast = false,
}: {
  data: T[]
  tableColumns: TableHeaderColumn[]
  includeActiveSorting?: boolean
  emptyValuesLast?: boolean
}) => {
  const [sortConfig, setSortConfig] = React.useState<{
    key: string
    direction: SortDirection
  } | null>(null)
  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return { key, direction: 'asc' }
      }
      if (prev.direction === 'asc') {
        return { key, direction: 'desc' }
      }
      return null
    })
  }

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      if (includeActiveSorting) {
        const aActive = a.isActive !== false
        const bActive = b.isActive !== false

        if (aActive && !bActive) return -1
        if (!aActive && bActive) return 1
      }
      if (sortConfig) {
        const { key, direction } = sortConfig
        const column = tableColumns.find((c) => c.key === key)
        const sortKey = column?.sortKey ?? key
        const getVal = (obj: any, path: string) =>
          path.split('.').reduce((acc, part) => acc?.[part], obj)

        const aVal = getVal(a, sortKey)
        const bVal = getVal(b, sortKey)

        if (emptyValuesLast) {
          const aEmpty = isEmptySortValue(aVal)
          const bEmpty = isEmptySortValue(bVal)
          if (aEmpty !== bEmpty) return aEmpty ? 1 : -1
          if (aEmpty && bEmpty) return 0
        }

        let compareResult = 0

        if (column?.type === 'date') {
          compareResult = parseDateValue(aVal) - parseDateValue(bVal)
        } else if (column?.type === 'duration') {
          compareResult = parseDurationValue(aVal) - parseDurationValue(bVal)
        } else if (column?.type === 'number') {
          compareResult = parseAmountValue(aVal) - parseAmountValue(bVal)
        } else {
          compareResult = (aVal ?? '')
            .toString()
            .toLowerCase()
            .localeCompare((bVal ?? '').toString().toLowerCase(), 'pl')
        }

        if (compareResult === 0 && column?.secondaryKey) {
          const aSecondary =
            getVal(a, column.secondaryKey)?.toString().toLowerCase() ?? ''
          const bSecondary =
            getVal(b, column.secondaryKey)?.toString().toLowerCase() ?? ''
          compareResult = aSecondary.localeCompare(bSecondary, 'pl')
        }

        return direction === 'asc' ? compareResult : -compareResult
      }

      return 0
    })
  }, [data, sortConfig, tableColumns, emptyValuesLast, includeActiveSorting])

  return { sortedData, sortConfig, setSortConfig, handleHeaderClick }
}
